import { helpWhy } from '../../help/why';
import React, { useEffect, useState } from 'react';
import {
    Box, Button, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography, Paper,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { IStyleCostLine, IStyleCostLineRequest, StyleCostCategory } from '../../interfaces/garments/style.interface';
import { formatMoney } from '../../utils/format';
import { enumLabel } from '../../utils/garmentsLabels';

const CATEGORIES: StyleCostCategory[] = ['FABRIC', 'TRIM', 'CM', 'COMMERCIAL', 'OTHER'];

interface EditableLine {
    key: string;
    category: StyleCostCategory;
    description: string;
    quantity: string;
    uom: string;
    unitCost: string;
}

const toEditable = (line: IStyleCostLine, index: number): EditableLine => ({
    key: line.id || `existing-${index}`,
    category: line.category,
    description: line.description,
    quantity: line.quantity != null ? String(line.quantity) : '',
    uom: line.uom ?? '',
    unitCost: line.unitCost != null ? String(line.unitCost) : '',
});

const blankLine = (key: string): EditableLine => ({
    key, category: 'FABRIC', description: '', quantity: '', uom: '', unitCost: '',
});

const lineAmount = (line: EditableLine): number => {
    const qty = parseFloat(line.quantity);
    const cost = parseFloat(line.unitCost);
    if (Number.isNaN(qty) || Number.isNaN(cost)) return 0;
    return qty * cost;
};

interface StyleCostSheetProps {
    costLines: IStyleCostLine[];
    totalCost: number;
    canManage: boolean;
    saving: boolean;
    onSave: (requests: IStyleCostLineRequest[]) => Promise<void>;
}

let keySeq = 0;
const nextKey = () => `new-${++keySeq}`;

/** Cost-sheet-lite editor hosted inside `StyleForm` — add/remove rows, live total, replace-all
 *  save (garments-merchandising T8). Mirrors `StyleService#replaceCostLines`'s semantics: Save
 *  sends the FULL current row set, which the backend swaps in wholesale. */
export function StyleCostSheet({ costLines, totalCost, canManage, saving, onSave }: StyleCostSheetProps) {
    const [lines, setLines] = useState<EditableLine[]>(() => costLines.map(toEditable));
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        setLines(costLines.map(toEditable));
        setDirty(false);
    }, [costLines]);

    const liveTotal = lines.reduce((sum, l) => sum + lineAmount(l), 0);

    const updateLine = (key: string, patch: Partial<EditableLine>) => {
        setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l)));
        setDirty(true);
    };

    const addLine = () => {
        setLines(prev => [...prev, blankLine(nextKey())]);
        setDirty(true);
    };

    const removeLine = (key: string) => {
        setLines(prev => prev.filter(l => l.key !== key));
        setDirty(true);
    };

    const handleSave = async () => {
        const requests: IStyleCostLineRequest[] = lines
            .filter(l => l.description.trim() !== '')
            .map((l, index) => ({
                category: l.category,
                description: l.description.trim(),
                quantity: l.quantity.trim() === '' ? null : parseFloat(l.quantity),
                uom: l.uom.trim() || null,
                unitCost: l.unitCost.trim() === '' ? null : parseFloat(l.unitCost),
                sortOrder: index,
            }));
        await onSave(requests);
        setDirty(false);
    };

    const saveWhy = saving ? null
        : !dirty ? 'Change a line first, there is nothing to save.'
        : null;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1">Cost Sheet</Typography>
                {canManage && (
                    <Button size="small" startIcon={<AddIcon />} data-help="merchandising.costSheet.add" onClick={addLine}>
                        Add Line
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="merchandising.costSheet.col.category">Category</TableCell>
                            <TableCell data-help="merchandising.costSheet.col.description">Description</TableCell>
                            <TableCell align="right" data-help="merchandising.costSheet.col.qty">Qty</TableCell>
                            <TableCell data-help="merchandising.costSheet.col.uom">UoM</TableCell>
                            <TableCell align="right" data-help="merchandising.costSheet.col.unitCost">Unit Cost</TableCell>
                            <TableCell align="right" data-help="merchandising.costSheet.col.amount">Amount</TableCell>
                            {canManage && <TableCell align="right" data-help="merchandising.costSheet.col.actions">Actions</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {lines.map(line => (
                            <TableRow key={line.key}>
                                <TableCell sx={{ minWidth: 120 }}>
                                    {canManage ? (
                                        <TextField
                                            data-help="merchandising.costSheet.col.category" select size="small" fullWidth value={line.category}
                                            onChange={e => updateLine(line.key, { category: e.target.value as StyleCostCategory })}
                                        >
                                            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{enumLabel(c)}</MenuItem>)}
                                        </TextField>
                                    ) : enumLabel(line.category)}
                                </TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                    {canManage ? (
                                        <TextField
                                            data-help="merchandising.costSheet.col.description" size="small" fullWidth value={line.description}
                                            onChange={e => updateLine(line.key, { description: e.target.value })}
                                        />
                                    ) : line.description}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 90 }}>
                                    {canManage ? (
                                        <TextField
                                            data-help="merchandising.costSheet.col.qty" size="small" type="number" value={line.quantity}
                                            onChange={e => updateLine(line.key, { quantity: e.target.value })}
                                        />
                                    ) : (line.quantity || '—')}
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    {canManage ? (
                                        <TextField
                                            data-help="merchandising.costSheet.col.uom" size="small" value={line.uom}
                                            onChange={e => updateLine(line.key, { uom: e.target.value })}
                                        />
                                    ) : (line.uom || '—')}
                                </TableCell>
                                <TableCell align="right" sx={{ minWidth: 100 }}>
                                    {canManage ? (
                                        <TextField
                                            data-help="merchandising.costSheet.col.unitCost" size="small" type="number" value={line.unitCost}
                                            onChange={e => updateLine(line.key, { unitCost: e.target.value })}
                                        />
                                    ) : (line.unitCost || '—')}
                                </TableCell>
                                <TableCell align="right">{formatMoney(lineAmount(line))}</TableCell>
                                {canManage && (
                                    <TableCell align="right">
                                        <IconButton data-help="merchandising.costSheet.remove" size="small" onClick={() => removeLine(line.key)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                )}
                            </TableRow>
                        ))}
                        {lines.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={canManage ? 7 : 6} align="center">No cost lines</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2">
                    Total: {formatMoney(liveTotal)}
                    {!dirty && ` (saved: ${formatMoney(totalCost)})`}
                </Typography>
                {canManage && (
                    <Button
                        variant="contained" size="small" data-help="merchandising.costSheet.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} onClick={handleSave}
                        disabled={saving || !dirty}
                    >
                        Save Cost Sheet
                    </Button>
                )}
            </Box>
        </Box>
    );
}

export default StyleCostSheet;
